import { useEffect, useRef, useCallback } from "react";
import { HubConnectionState } from "@microsoft/signalr";
import { getSharedConnection } from "../signalrConnection";

export interface DataRefreshEvent {
  scope: string;
  entityId?: string;
  action?: string;
}

interface UseDataRefreshOptions {
  entityId?: string;
  debounceMs?: number;
}

/**
 * Subscribes to DataRefresh SignalR events for a specific scope.
 * When a matching event is received, calls the callback after debouncing.
 *
 * @param scope - The scope to listen for (e.g. "serviceBoard", "myOffers")
 * @param callback - Function to call when a matching DataRefresh event arrives
 * @param options - Optional entityId filter and debounce duration (default 2000ms)
 *
 * Usage:
 *   useDataRefresh("serviceBoard", () => {
 *     dispatch(getPublicServiceRequests(query));
 *   });
 *
 *   useDataRefresh("receivedOffers", () => {
 *     dispatch(getReceivedOffers(serviceRequestId));
 *   }, { entityId: serviceRequestId });
 */
const useDataRefresh = (
  scope: string,
  callback: (event: DataRefreshEvent) => void,
  options?: UseDataRefreshOptions
) => {
  const debounceMs = options?.debounceMs ?? 2000;
  const entityId = options?.entityId;

  // Keep callback ref current without re-subscribing
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  // Accumulate events during debounce window so the callback
  // receives the most recent event data
  const pendingEventRef = useRef<DataRefreshEvent | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const debouncedCallback = useCallback((event: DataRefreshEvent) => {
    pendingEventRef.current = event;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      if (pendingEventRef.current) {
        callbackRef.current(pendingEventRef.current);
        pendingEventRef.current = null;
      }
      timerRef.current = null;
    }, debounceMs);
  }, [debounceMs]);

  useEffect(() => {
    const handler = (event: DataRefreshEvent) => {
      // Only respond to events matching our scope
      if (event.scope !== scope) return;

      // If entityId filter is specified, only match that entity
      if (entityId && event.entityId !== entityId) return;

      debouncedCallback(event);
    };

    // Try to subscribe immediately if connection exists
    const connection = getSharedConnection();
    if (connection && connection.state === HubConnectionState.Connected) {
      connection.on("DataRefresh", handler);
    }

    // Also poll for connection availability (handles race condition
    // where useDataRefresh mounts before useNotificationHub connects)
    let pollInterval: ReturnType<typeof setInterval> | null = null;
    let subscribed = false;

    if (!connection || connection.state !== HubConnectionState.Connected) {
      pollInterval = setInterval(() => {
        const conn = getSharedConnection();
        if (conn && !subscribed) {
          conn.on("DataRefresh", handler);
          subscribed = true;
          if (pollInterval) {
            clearInterval(pollInterval);
            pollInterval = null;
          }
        }
      }, 500);
    } else {
      subscribed = true;
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      if (pollInterval) {
        clearInterval(pollInterval);
      }
      const conn = getSharedConnection();
      if (conn) {
        conn.off("DataRefresh", handler);
      }
    };
  }, [scope, entityId, debouncedCallback]);
};

export default useDataRefresh;
